import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import { CheckCircle, ArrowDownward, RadioButtonUnchecked } from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';

interface ActivateSessionDialogProps {
  open: boolean;
  target: Session | null;
  currentActive: Session | null;
  onClose: () => void;
  onActivated: (session: Session) => void;
}

const SessionCard: React.FC<{
  session: Session;
  badge: string;
  badgeColor: 'success' | 'info' | 'neutral';
}> = ({ session, badge, badgeColor }) => {
  const color = badgeColor === 'success' ? 'success.main'
              : badgeColor === 'info'    ? 'info.main'
              : 'text.secondary';

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        {badgeColor === 'success' && <CheckCircle sx={{ color, fontSize: 18 }} />}
        {badgeColor !== 'success' && <RadioButtonUnchecked sx={{ color, fontSize: 18 }} />}
        <Typography variant="caption" sx={{ color, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
          {badge}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>{session.name}</Typography>
      <Typography variant="body2" color="text.secondary">
        {format(new Date(session.startDate), 'MMM d, yyyy')} → {format(new Date(session.endDate), 'MMM d, yyyy')}
      </Typography>
    </Paper>
  );
};

const ActivateSessionDialog: React.FC<ActivateSessionDialogProps> = ({
  open, target, currentActive, onClose, onActivated
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(3);

  useEffect(() => {
    if (!open) return;
    setCooldown(3);
    const interval = setInterval(() => {
      setCooldown(c => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [open]);

  const handleConfirm = async () => {
    if (!target) return;
    setSubmitting(true);
    try {
      const updated = await sessionsApi.activate(target._id);
      enqueueSnackbar(`"${updated.name}" is now active`, { variant: 'success' });
      onActivated(updated);
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not activate session', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!target) return null;
  const sameTarget = currentActive && currentActive._id === target._id;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>Activate session</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          The active session is where new enrollments and writes are created. You can switch back later.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={1.5}>
          {currentActive && !sameTarget && (
            <>
              <SessionCard session={currentActive} badge="Currently active" badgeColor="info" />
              <Box display="flex" justifyContent="center">
                <ArrowDownward sx={{ color: 'text.disabled' }} />
              </Box>
            </>
          )}
          <SessionCard session={target} badge={sameTarget ? 'Already active' : 'Will become active'} badgeColor="success" />

          {currentActive && !sameTarget && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{currentActive.name}</strong> stays in <em>active</em> status during the transition. Mark it
                Complete from its row when you’re ready — no data is lost.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disabled={submitting || cooldown > 0 || sameTarget === true}
          onClick={handleConfirm}
          disableElevation
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Activating…' : cooldown > 0 ? `Activate (${cooldown})` : 'Activate'}
        </Button>
      </DialogActions>
      <Divider sx={{ display: 'none' }} />
    </Dialog>
  );
};

export default ActivateSessionDialog;
